import { useState } from "react";
import { ClipboardList, Wand2 } from "lucide-react";
import { api } from "@/data/api";
import { npmApiErrorMessage } from "@/data/npmState";
import { useAsync } from "@/hooks/useAsync";
import { SERVICE_KIND_LABEL, STACK_CLASS_LABEL } from "@/lib/health";
import { PageHeader, AsyncBoundary, RowsSkeleton, DataTable, Panel, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const OWNERSHIP = {
  CONFIRMED: { status: "healthy", label: "Confirmado" }, DERIVABLE: { status: "pending", label: "Derivável" },
  UNASSIGNED: { status: "unknown", label: "Não atribuído" }, CONFLICT: { status: "error", label: "Conflito" },
};
const RECORD = {
  ACTIVE: { status: "healthy", label: "Ativo" }, UNMANAGED: { status: "unknown", label: "Não atribuído" },
  STALE: { status: "warning", label: "Stale" }, MISSING: { status: "missing", label: "Ausente" }, DUPLICATE: { status: "error", label: "Duplicado" },
};
export const INVENTORY_FILTERS = [
  ["all", "Todos"], ["owned", "Com Project"], ["unassigned", "Não atribuídos"], ["internal", "Internos"], ["external", "Client externo"],
  ["APPLICATION", "Aplicações"], ["PLATFORM_INTERNAL", "Plataforma interna"], ["INFRASTRUCTURE", "Infraestrutura"],
  ["SHARED_SERVICE", "Serviços compartilhados"], ["review", "Needs review"], ["STALE", "Registros stale"], ["MISSING", "Registros ausentes"],
];
const RECORD_FILTERS = new Set(["STALE", "MISSING"]);

export function stackMatches(stack, filter) {
  switch (filter) {
    case "all": return true;
    case "owned": return stack.ownership === "CONFIRMED" || stack.ownership === "DERIVABLE";
    case "unassigned": return stack.ownership === "UNASSIGNED";
    case "internal": return stack.projects.length > 0 && stack.internal;
    case "external": return stack.projects.length > 0 && !stack.internal;
    case "review": return stack.needs_review || stack.ownership === "CONFLICT";
    default: return stack.classification === filter;
  }
}

export function recommendedAction(stack) {
  if (stack.ownership === "CONFLICT") return "Resolver conflito de ownership";
  if (stack.ownership === "DERIVABLE") return "Derivar ownership (mesmo stack)";
  if (stack.ownership === "CONFIRMED") return "—";
  if (stack.needs_review) return "Revisar: atribuir ao Project correto";
  if (stack.classification === "UNKNOWN") return "Classificar";
  if (stack.classification === "APPLICATION") return "Criar Project interno ou atribuir a Project";
  return "Nenhuma (não vira Project)";
}

const keyOf = (s) => ({ server_id: s.server_id, compose_project: s.compose_project, working_dir: s.working_dir });

export default function Inventory() {
  const q = useAsync(() => Promise.all([api.getInventory(), api.listProjectsRaw(), api.getServersUsage().catch(() => [])])
    .then(([inventory, projects, servers]) => ({ inventory, projects, servers })), []);
  const [filter, setFilter] = useState("all");
  const [plan, setPlan] = useState(null);
  const [bootstrap, setBootstrap] = useState(null);
  const [message, setMessage] = useState(null);
  const data = q.data?.inventory;
  const projects = q.data?.projects || [];

  const act = async (fn, ok) => {
    setMessage(null);
    try {
      const result = await fn();
      if (ok) setMessage({ ok: true, text: ok(result) });
      q.reload();
      return result;
    } catch (e) {
      setMessage({ ok: false, text: npmApiErrorMessage(e) });
      return null;
    }
  };
  const derive = async (apply) => {
    const result = await act(() => api.deriveOwnership(apply), (r) => (apply ? `${r.applied.length} associação(ões) aplicada(s).` : null));
    if (result) setPlan(result);
  };
  const openBootstrap = async (stack) => {
    setMessage(null);
    try {
      setBootstrap({ stack, plan: await api.stackBootstrapPlan(keyOf(stack)) });
    } catch (e) {
      setMessage({ ok: false, text: npmApiErrorMessage(e) });
    }
  };
  const confirmBootstrap = async () => {
    const { stack } = bootstrap;
    setBootstrap(null);
    await act(() => api.bootstrapStack(keyOf(stack)), (r) => `Project "${stack.compose_project}" criado (interno)${r.site_id ? " com Site" : ""}${r.site_needs_review ? " — Site precisa de revisão" : ""}.`);
  };

  const stacks = (data?.stacks || []).filter((s) => stackMatches(s, filter));
  const records = (data?.records || []).filter((r) => (RECORD_FILTERS.has(filter) ? r.state === filter : !["ACTIVE", "UNMANAGED"].includes(r.state)));

  return <div className="space-y-6 animate-fade-up">
    <PageHeader icon={ClipboardList} title="Inventário & Ownership" description="Registros da TintiCloud comparados com o que o Agent reporta hoje. Classificação não é ownership; nada é removido."
      actions={<Button variant="outline" onClick={() => derive(false)} data-testid="inventory-derive"><Wand2 className="h-4 w-4" /> Derivar ownership (simulação)</Button>} />
    <AsyncBoundary loading={q.loading} error={q.error} skeleton={<RowsSkeleton />}>
      {data && <>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8" data-testid="inventory-summary">
          {[["Registros", data.summary.records], ["Atuais", data.summary.current], ["Com Project", data.summary.ACTIVE], ["Não atribuídos", data.summary.UNMANAGED],
            ["Stale", data.summary.STALE], ["Ausentes", data.summary.MISSING], ["Stacks", data.summary.stacks], ["Needs review", data.summary.needs_review]].map(([k, v]) =>
            <div key={k} className="rounded-lg border border-line bg-surface px-3 py-2"><p className="text-xs text-muted-foreground">{k}</p><p className="font-heading text-xl font-bold text-foreground">{v}</p></div>)}
        </div>

        {plan && <Panel title={plan.dry_run ? "Simulação de derivação" : "Derivação aplicada"} bodyClassName="space-y-2 text-sm">
          <p className="text-muted-foreground">Evidência: mesmo servidor + mesmo compose project + mesmo diretório de um container já pertencente ao Project. Nomes nunca são usados.</p>
          {(plan.dry_run ? plan.planned : plan.applied).map((p) => <div key={p.container_id} className="font-mono text-xs">{p.container} → {p.project}</div>)}
          {!plan.planned.length && <p>Nenhuma associação derivável.</p>}
          {plan.conflicts.length > 0 && <p className="text-err">{plan.conflicts.length} conflito(s) — não aplicados.</p>}
          {plan.dry_run && plan.planned.length > 0 && <Button size="sm" onClick={() => derive(true)} data-testid="inventory-derive-apply">Aplicar {plan.planned.length}</Button>}
        </Panel>}
        {message?.text && <p role="alert" className={`rounded-lg border px-3 py-2 text-sm ${message.ok ? "border-ok/30 text-ok" : "border-err/30 text-err"}`}>{message.text}</p>}

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">Filtro</span>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded border border-line bg-background px-2 py-1 text-sm" data-testid="inventory-filter">
            {INVENTORY_FILTERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>

        {!RECORD_FILTERS.has(filter) && <Panel title={`Stacks (${stacks.length})`} noPadding>
          <DataTable testid="inventory-stacks" rows={stacks.map((s) => ({ ...s, id: `${s.server_id}/${s.compose_project}/${s.working_dir}` }))} columns={[
            { key: "compose_project", header: "Stack", render: (s) => <div><p className="font-medium text-foreground">{s.compose_project}</p><p className="font-mono text-[11px] text-muted-foreground">{s.server} · {s.working_dir}</p></div> },
            { key: "classification", header: "Tipo", render: (s) => <select value={s.classification} onChange={(e) => act(() => api.classifyStack(keyOf(s), e.target.value, s.needs_review, s.note))} className="rounded border border-line bg-background px-1 py-0.5 text-xs" aria-label={`Classificar ${s.compose_project}`}>
              {Object.entries(STACK_CLASS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select> },
            { key: "projects", header: "Project / Client", render: (s) => s.projects.length ? <span>{s.projects.join(", ")}<span className="block text-[11px] text-muted-foreground">{s.clients.join(", ")}{s.internal ? " · Interno" : ""}</span></span> : "—" },
            { key: "npm_domains", header: "Domínios (NPM)", render: (s) => <span className="font-mono text-xs">{s.npm_domains.join(", ") || "—"}</span> },
            { key: "containers", header: "Serviços", render: (s) => <span className="text-xs">{s.containers.map((c) => `${SERVICE_KIND_LABEL[c.kind] || c.kind}${c.engine ? ` (${c.engine})` : ""}`).join(", ")}</span> },
            { key: "ownership", header: "Ownership", render: (s) => <div className="space-y-1"><StatusBadge {...OWNERSHIP[s.ownership]} size="sm" pulse={false} />{s.needs_review && <StatusBadge status="warning" label="Needs review" size="sm" pulse={false} />}</div> },
            { key: "action", header: "Ação recomendada", render: (s) => <div className="space-y-1 text-xs">
              <p className="text-muted-foreground">{recommendedAction(s)}</p>
              {s.note && <p className="text-[11px] text-muted-foreground">{s.note}</p>}
              <div className="flex flex-wrap gap-1">
                <Button size="sm" variant="ghost" onClick={() => act(() => api.classifyStack(keyOf(s), s.classification, !s.needs_review, s.note))}>{s.needs_review ? "Revisado" : "Marcar revisão"}</Button>
                {s.ownership === "UNASSIGNED" && s.classification === "APPLICATION" && !s.needs_review &&
                  <Button size="sm" variant="outline" onClick={() => openBootstrap(s)} data-testid={`bootstrap-${s.compose_project}`}>Criar Project interno</Button>}
                {s.ownership === "UNASSIGNED" && <select defaultValue="" onChange={(e) => e.target.value && act(() => api.assignStack(keyOf(s), e.target.value), (r) => `${r.adopted.length} container(s) atribuído(s).`)} className="rounded border border-line bg-background px-1 py-0.5 text-xs" aria-label={`Atribuir ${s.compose_project}`}>
                  <option value="">Atribuir a Project…</option>
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>}
              </div>
            </div> },
          ]} />
        </Panel>}

        <Panel title="Servidores" noPadding>
          <DataTable testid="inventory-servers" rows={q.data.servers} columns={[
            { key: "hostname", header: "Servidor", render: (s) => <div><p className="font-medium text-foreground">{s.hostname}</p><p className="text-[11px] text-muted-foreground">criado {new Date(s.created_at).toLocaleDateString("pt-BR")} · {s.connection_status}</p></div> },
            { key: "usage", header: "Uso", render: (s) => <StatusBadge status={s.usage === "IN_USE" ? "healthy" : "warning"} label={s.usage === "IN_USE" ? "Em uso" : "Unused / Safe to remove"} size="sm" pulse={false} /> },
            { key: "projects", header: "Projects", render: (s) => s.projects.join(", ") || "—" },
            { key: "current_containers", header: "Containers", render: (s) => `${s.current_containers} atuais · ${s.stale_records} stale` },
            { key: "reasons", header: "Evidência", render: (s) => <span className="text-xs text-muted-foreground">{s.reasons.join(", ")}</span> },
          ]} />
        </Panel>

        <Panel title="Registros de containers" noPadding>
          <DataTable testid="inventory-records" rows={records} columns={[
            { key: "name", header: "Registro", render: (r) => <div><p className="font-mono text-xs text-foreground">{r.name}</p><p className="font-mono text-[11px] text-muted-foreground">{r.docker_id} · {r.server}</p></div> },
            { key: "state", header: "Estado", render: (r) => <StatusBadge {...RECORD[r.state]} size="sm" pulse={false} /> },
            { key: "project", header: "Project", render: (r) => r.project || "—" },
            { key: "missing_since", header: "Ausente desde", render: (r) => (r.missing_since ? new Date(r.missing_since).toLocaleString("pt-BR") : "—") },
            { key: "action", header: "Ação recomendada", render: (r) => <span className="text-xs text-muted-foreground">{r.action}</span> },
          ]} />
        </Panel>
      </>}
    </AsyncBoundary>

    <Dialog open={!!bootstrap} onOpenChange={(o) => !o && setBootstrap(null)}>
      <DialogContent className="max-w-lg border-line bg-popover">
        <DialogHeader>
          <DialogTitle>Criar Project interno · {bootstrap?.stack.compose_project}</DialogTitle>
          <DialogDescription>Project do Client interno + Environment production no servidor do stack + adoção dos containers atuais. NPM/DNS/certificados não são alterados.</DialogDescription>
        </DialogHeader>
        {bootstrap && <div className="space-y-2 text-sm" data-testid="bootstrap-plan">
          <p>Containers: <span className="font-mono text-xs">{bootstrap.plan.containers.join(", ")}</span></p>
          <p>Site: {bootstrap.plan.site ? `${bootstrap.plan.site.primary_domain || "sem domínio principal (revisão)"} · NPM ${bootstrap.plan.site.npm_managed ? "gerenciado" : "externo/não gerenciado"}` : bootstrap.plan.npm_hosts > 1 ? "vários hosts NPM — Site para revisão" : "nenhum host NPM — sem Site"}</p>
          {bootstrap.plan.blockers.length > 0 && <p className="text-err">Bloqueado: {bootstrap.plan.blockers.join("; ")}</p>}
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => setBootstrap(null)}>Cancelar</Button>
            <Button size="sm" disabled={bootstrap.plan.blockers.length > 0} onClick={confirmBootstrap} data-testid="bootstrap-confirm">Confirmar</Button>
          </div>
        </div>}
      </DialogContent>
    </Dialog>
  </div>;
}
